import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs-dist/build/pdf.worker.min.mjs';

interface PdfPreviewOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  targetWidth?: number;
  targetHeight?: number;
  cropMode?: 'fit' | 'top-crop';
}

@Injectable({ providedIn: 'root' })
export class PdfPreviewService {
  async generatePreviewFromFile(file: File, options?: PdfPreviewOptions): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    return this.generatePreview({ data: new Uint8Array(arrayBuffer) }, options);
  }

  async generatePreviewFromUrl(url: string, options?: PdfPreviewOptions): Promise<string> {
    return this.generatePreview(url, options);
  }

  private async generatePreview(source: string | { data: Uint8Array }, options?: PdfPreviewOptions): Promise<string> {
    if (typeof document === 'undefined') {
      throw new Error('PDF preview generation is only available in the browser.');
    }

    const pdf = await pdfjsLib.getDocument(source as any).promise;

    try {
      const page = await pdf.getPage(1);
      const initialViewport = page.getViewport({ scale: 1 });
      const cropMode = options?.cropMode ?? 'fit';
      const maxWidth = options?.maxWidth ?? 550;
      const maxHeight = options?.maxHeight ?? 300;
      const quality = options?.quality ?? 0.9;
      const targetWidth = options?.targetWidth;
      const targetHeight = options?.targetHeight;
      const oversample = 2;
      const scale = cropMode === 'top-crop' && targetWidth
        ? (targetWidth * oversample) / initialViewport.width
        : Math.min(maxWidth / initialViewport.width, maxHeight / initialViewport.height, 2);
      const viewport = page.getViewport({ scale: Number.isFinite(scale) && scale > 0 ? scale : 1 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Could not create canvas context for PDF preview.');
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport,
        canvas
      }).promise;

      if (cropMode === 'top-crop' && targetWidth && targetHeight) {
        const outputCanvas = document.createElement('canvas');
        const outputContext = outputCanvas.getContext('2d');

        if (!outputContext) {
          throw new Error('Could not create output canvas context for PDF preview.');
        }

        outputCanvas.width = targetWidth;
        outputCanvas.height = targetHeight;

        outputContext.drawImage(
          canvas,
          0,
          0,
          canvas.width,
          Math.min(canvas.height, targetHeight * oversample),
          0,
          0,
          targetWidth,
          targetHeight
        );

        return outputCanvas.toDataURL('image/jpeg', quality);
      }

      return canvas.toDataURL('image/jpeg', quality);
    } finally {
      pdf.destroy();
    }
  }
}
